import { formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz';
import { formatDistanceToNow as fnsDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

/**
 * Todas las fechas del sistema se muestran en hora de Colombia.
 *
 * El problema que arregla: la base guarda instantes en UTC —que es lo
 * correcto— y el navegador los pintaba con la zona del equipo. La misma
 * suscripción se veía vencer «el 30 de septiembre» en Bucaramanga y «el 1 de
 * octubre» en un equipo configurado en otra zona o en cualquier contenedor:
 *
 *   guardado  2026-10-01T04:59:59Z
 *   Bogotá    30/09/26 11:59 p.m.   ← lo correcto
 *   Madrid     1/10/26  6:59 a.m.
 *   UTC        1/10/26  4:59 a.m.
 *
 * El negocio es colombiano: el corte de caja, el vencimiento de la licencia y
 * la agenda son de Colombia, no del reloj de quien mira. Anclar la zona aquí
 * hace que la fecha signifique lo mismo para todos.
 *
 * Colombia no tiene horario de verano (UTC-5 todo el año), pero se usa el
 * nombre de la zona y no un desfase fijo: si algún día cambia, lo hace la
 * base de datos de zonas horarias y no hay que tocar el código.
 */
export const ZONA = 'America/Bogota';

/** Lo que llega de la base: ISO, Date, o nada. */
type Fecha = Date | string | number | null | undefined;

function aDate(f: Fecha): Date | null {
  if (f === null || f === undefined || f === '') return null;
  const d = f instanceof Date ? f : new Date(f);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Formatea en hora de Colombia con los patrones de date-fns.
 * Devuelve '—' ante una fecha vacía o inválida: una pantalla con un guion se
 * lee, una con «Invalid Date» parece rota.
 */
export function fecha(f: Fecha, patron = "d 'de' MMM yyyy"): string {
  const d = aDate(f);
  if (!d) return '—';
  return formatInTimeZone(d, ZONA, patron, { locale: es });
}

/** 30 de sep 2026, 11:59 p. m. */
export function fechaYHora(f: Fecha): string {
  return fecha(f, "d 'de' MMM yyyy · h:mm a");
}

/** Solo la hora: 11:59 p. m. */
export function hora(f: Fecha): string {
  return fecha(f, 'h:mm a');
}

/** Para <input type="date">, que exige exactamente aaaa-mm-dd. */
export function fechaISO(f: Fecha): string {
  const d = aDate(f);
  if (!d) return '';
  return formatInTimeZone(d, ZONA, 'yyyy-MM-dd');
}

/** «hace 3 días», «en 7 días». */
export function haceCuanto(f: Fecha): string {
  const d = aDate(f);
  if (!d) return '—';
  return fnsDistanceToNow(d, { locale: es, addSuffix: true });
}

/** El día de hoy en Colombia, no en el reloj del equipo. */
export function hoyEnColombia(): Date {
  return toZonedTime(new Date(), ZONA);
}

/** aaaa-mm-dd de hoy en Colombia. Es la clave de los cortes diarios. */
export function hoyISO(): string {
  return formatInTimeZone(new Date(), ZONA, 'yyyy-MM-dd');
}

/**
 * Convierte lo que el usuario escribió —que él piensa en hora de Colombia— al
 * instante real que se guarda.
 *
 * Sin esto, agendar «el 30 a las 8:00» desde un equipo en otra zona guarda
 * otra hora, y la cita aparece corrida en la agenda de la peluquería.
 */
export function desdeColombia(fechaLocal: string, horaLocal = '00:00'): Date {
  return fromZonedTime(`${fechaLocal} ${horaLocal}`, ZONA);
}

/**
 * El último instante de un día de Colombia: 23:59:59 hora local.
 * Es la frontera de la licencia y del ciclo de facturación —el cliente paga
 * «hasta el 30», y el 30 cuenta entero.
 */
export function finDelDiaEnColombia(fechaLocal: string): Date {
  return fromZonedTime(`${fechaLocal} 23:59:59`, ZONA);
}

/**
 * Días completos que faltan para una fecha, contados por día de calendario
 * colombiano y no por múltiplos de 24 horas.
 *
 * Importa para el aviso de vencimiento: a las 11 p.m. del día 23, la licencia
 * que vence el 30 tiene 7 días por calendario pero 6,04 por resta de horas.
 * Redondear hacia abajo avisaría un día tarde.
 */
export function diasHasta(f: Fecha): number | null {
  const d = aDate(f);
  if (!d) return null;
  const objetivo = formatInTimeZone(d, ZONA, 'yyyy-MM-dd');
  const hoy = hoyISO();
  const ms = Date.parse(`${objetivo}T00:00:00Z`) - Date.parse(`${hoy}T00:00:00Z`);
  return Math.round(ms / 86_400_000);
}

/**
 * Los instantes que delimitan un rango de días de Colombia, para filtrar una
 * columna `timestamptz`: desde la medianoche del primer día hasta ANTES de la
 * medianoche del día siguiente al último.
 *
 * Comparar la columna con la fecha suelta (`lte('spent_at', '2026-10-13')`)
 * corta en el 13 a las 00:00 UTC: el último día del rango se quedaba fuera
 * entero —un gasto de hoy no salía en «Este mes»— y lo de las 7 p. m. en
 * adelante del día anterior al rango entraba sin ser suyo.
 */
export function limitesDeDias(desde: string, hasta: string): { desde: string; antesDe: string } {
  const siguiente = new Date(Date.parse(`${hasta.slice(0, 10)}T00:00:00Z`) + 86_400_000)
    .toISOString().slice(0, 10);
  return {
    desde: desdeColombia(desde.slice(0, 10)).toISOString(),
    antesDe: desdeColombia(siguiente).toISOString(),
  };
}

/**
 * Un DÍA de calendario («2030-01-19», una columna `date`), no un instante.
 *
 * `fecha('2030-01-19')` lo lee como medianoche UTC, que en Colombia es el
 * 18 a las 7 p. m.: la vigencia de una resolución de la DIAN salía un día
 * antes de la real. Se ancla a medianoche de Colombia antes de pintarlo.
 */
export function diaCalendario(dia: string | null | undefined, patron = "d 'de' MMM yyyy"): string {
  const d = (dia ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return '—';
  return fecha(desdeColombia(d), patron);
}
